import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, Upload, Loader2, Sparkles } from "lucide-react";

const NICHES = [
  { value: "pressure_washing", label: "Pressure washing" },
  { value: "landscaping", label: "Landscaping" },
  { value: "painting", label: "Painting" },
  { value: "roofing", label: "Roofing" },
  { value: "mobile_detailing", label: "Mobile detailing" },
];

async function fileToBase64(file) {
  return await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function NewEstimate() {
  const nav = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef();
  const [niche, setNiche] = useState(user?.niche || "pressure_washing");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPEG, PNG, or WEBP supported");
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Image too large — keep it under 6MB");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const analyze = async () => {
    if (!file) { toast.error("Snap or upload a photo first"); return; }
    setLoading(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/estimates/analyze", {
        niche, photo_base64: b64, address, notes,
      });
      toast.success("Estimate ready!");
      nav(`/app/estimates/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-primary mb-1">Estimator</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">New AI estimate</h1>
        <p className="text-muted-foreground mt-2">Photo → measurements → quote in seconds.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bento-card p-5">
          <Label className="text-sm font-semibold mb-2 block">1. Photo</Label>
          <input
            ref={inputRef}
            data-testid="photo-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            onChange={onPick}
          />
          {preview ? (
            <div className="relative border border-border rounded-sm overflow-hidden mb-3">
              <img src={preview} alt="preview" className="w-full h-64 object-cover" />
            </div>
          ) : (
            <div className="border-2 border-dashed border-border rounded-sm h-64 flex flex-col items-center justify-center mb-3 bg-muted/40">
              <Camera className="w-10 h-10 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No photo yet</p>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              data-testid="capture-photo-btn"
              type="button"
              variant="outline"
              className="flex-1 h-12 rounded-sm"
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-2" /> {preview ? "Change photo" : "Snap / Upload"}
            </Button>
          </div>
        </div>

        <div className="bento-card p-5 space-y-4">
          <div>
            <Label className="text-sm font-semibold">2. Job type</Label>
            <Select value={niche} onValueChange={setNiche}>
              <SelectTrigger data-testid="niche-select" className="h-12 rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NICHES.map((n) => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-semibold">Address (optional)</Label>
            <Input data-testid="address-input" className="h-12 rounded-sm mt-1" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Oak Ln, Tampa FL" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Notes for AI (optional)</Label>
            <Textarea data-testid="notes-input" className="rounded-sm mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Heavy staining on left side, customer wants sealant add-on." rows={4} />
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          data-testid="analyze-btn"
          size="lg"
          className="h-14 px-8 rounded-sm text-base btn-kinetic"
          onClick={analyze}
          disabled={loading}
        >
          {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyzing photo…</> : <><Sparkles className="w-5 h-5 mr-2" /> Generate estimate</>}
        </Button>
      </div>
    </div>
  );
}
